import { useEffect, useMemo, useState } from 'react';
import useCancelRequest from '@intelligence/hooks/useCancelRequest';
import type { IStoreRankDetailsResData } from '@/pages/intelligence/types/storeRank';
import {
  fetchMiniGameDetailBasicInfo,
  fetchMiniGameDetailMetrics,
} from '../services';
import {
  buildBasicInfoPayload,
  buildMiniGameMetricsParams,
  getMiniGameAppIds,
  normalizeMiniGameBasicInfo,
  normalizeMiniGameMetrics,
} from '../utils';

import type { IMiniGameDetailsDataProps } from '../types';

const EMPTY_METRICS_PARAMS = {
  entity_type: 'minigame',
  app_ids: [],
  metrics: [],
  minigame_types: [],
  markets: ['global'],
  granularity: 'daily',
  group_by: ['game_id', 'date', 'minigame_type'],
  start_date: '',
  end_date: '',
  page: 1,
  page_size: 10000,
  filter: { minigame_genre: [] },
};

export default function useMiniGameDetailsData(
  props: IMiniGameDetailsDataProps,
) {
  const { gameId, dateRange, minigameTypes } = props;
  const basicInfoRequest = useCancelRequest();
  const metricsRequest = useCancelRequest();
  const [basicInfo, setBasicInfo] = useState<Record<string, any> | null>(null);
  const [basicInfoLoading, setBasicInfoLoading] = useState(Boolean(gameId));
  const [metricsData, setMetricsData] = useState<IStoreRankDetailsResData[]>([]);
  const [metricsLoading, setMetricsLoading] = useState(false);

  const appIds = useMemo(() => {
    const ids = getMiniGameAppIds(basicInfo);
    // basicInfo 未返回 app id 时回退使用 URL 的 gameId
    if (ids.length > 0) return ids;
    return gameId ? [gameId] : [];
  }, [basicInfo, gameId]);

  const metricsRequestParams = useMemo(() => {
    if (!appIds.length || !dateRange?.startDate || !dateRange?.endDate || !minigameTypes?.length) {
      return EMPTY_METRICS_PARAMS;
    }
    return buildMiniGameMetricsParams({
      appIds,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      minigameTypes,
    });
  }, [appIds, dateRange?.startDate, dateRange?.endDate, minigameTypes]);

  useEffect(() => {
    const token = basicInfoRequest.next();
    if (!gameId) {
      setBasicInfo(null);
      setBasicInfoLoading(false);
      return undefined;
    }

    setBasicInfoLoading(true);
    const payload = buildBasicInfoPayload(gameId);
    
    fetchMiniGameDetailBasicInfo(
      payload,
      { cancelToken: token.cancelToken },
    )
      .then(response => {
        if (token.isStale()) return;
        setBasicInfo(normalizeMiniGameBasicInfo(response));
      })
      .catch(() => {
        if (!token.isStale()) setBasicInfo(null);
      })
      .finally(() => {
        if (!token.isStale()) setBasicInfoLoading(false);
      });

    return undefined;
  }, [gameId]);

  useEffect(() => {
    const token = metricsRequest.next();
    if (
      basicInfoLoading ||
      !appIds.length ||
      !dateRange?.startDate ||
      !dateRange?.endDate
    ) {
      setMetricsData([]);
      setMetricsLoading(false);
      return undefined;
    }

    setMetricsLoading(true);
    fetchMiniGameDetailMetrics(metricsRequestParams, {
      cancelToken: token.cancelToken,
    })
      .then(response => {
        if (token.isStale()) return;
        setMetricsData(normalizeMiniGameMetrics(response));
      })
      .catch(() => {
        if (!token.isStale()) setMetricsData([]);
      })
      .finally(() => {
        if (!token.isStale()) setMetricsLoading(false);
      });

    return undefined;
  }, [
    appIds.length,
    basicInfoLoading,
    dateRange?.startDate,
    dateRange?.endDate,
    metricsRequestParams,
  ]);

  return {
    basicInfo,
    basicInfoLoading,
    metricsData,
    metricsLoading,
    metricsRequestParams,
  };
}
